import { useConfirm } from "../context/ConfirmContext";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { getProducts, createProduct, updateProduct, deleteProduct } from "../api";
import { primaryBtnStyle } from "./SalesDocShared";

export default function ProductsPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [form, setForm] = useState({ name: "", description: "", unit_price: "" });
  const [rowForm, setRowForm] = useState({ name: "", description: "", unit_price: "" });

  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => getProducts() });

  const createMut = useMutation({
    mutationFn: (data: object) => createProduct(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); setShowForm(false); setForm({ name: "", description: "", unit_price: "" }); },
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: object }) => updateProduct(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); setEditingProduct(null); },
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });

  const filtered = (products as any[]).filter((p: any) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <LoadingOverlay active={createMut.isPending || updateMut.isPending || deleteMut.isPending} />
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Products</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 14 }}>Product / service catalog used to quickly fill line items on Quotations, Billing Notes, and Tax Invoices</p>
        </div>
        <button onClick={() => { setEditingProduct(null); setForm({ name: "", description: "", unit_price: "" }); setShowForm(s => !s); }} style={primaryBtnStyle}>
          {showForm ? "Cancel" : "+ Add Product"}
        </button>
      </div>

      {showForm && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, marginBottom: 20 }}>
          <h3 style={{ margin: "0 0 16px", color: "#1e3a8a", fontSize: 16 }}>New Product</h3>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Product / Service Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. DevOps, Web Design, Consulting" style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Default Unit Price</label>
              <input type="number" min={0} step="0.01" value={form.unit_price} onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))}
                placeholder="0.00" style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Description / Details</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the service or product in detail…" rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 14, resize: "vertical", boxSizing: "border-box" }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button disabled={createMut.isPending} onClick={() => {
              if (!form.name.trim()) return;
              createMut.mutate({ name: form.name.trim(), description: form.description.trim(), unit_price: parseFloat(form.unit_price) || 0 });
            }} style={{ padding: "8px 20px", background: "#1e3a8a", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
              {createMut.isPending ? "Saving…" : "Create Product"}
            </button>
            <button onClick={() => setShowForm(false)} style={{ padding: "8px 16px", background: "none", border: "1px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontSize: 14, color: "#64748b" }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 10, alignItems: "center" }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13, width: 260 }} />
          {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", cursor: "pointer", color: "#94a3b8", fontSize: 13 }}>✕</button>}
          <span style={{ marginLeft: "auto", fontSize: 13, color: "#94a3b8" }}>{filtered.length} items</span>
        </div>
        <div className="table-wrap"><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12 }}>Name</th>
              <th style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#475569", fontSize: 12 }}>Description</th>
              <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#475569", fontSize: 12 }}>Unit Price</th>
              <th style={{ padding: "10px 16px", textAlign: "right", fontWeight: 600, color: "#475569", fontSize: 12 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p: any) => (
              editingProduct?.id === p.id ? (
                <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9", background: "#fafafa" }}>
                  <td style={{ padding: "8px 16px" }}>
                    <input value={rowForm.name} onChange={e => setRowForm(f => ({ ...f, name: e.target.value }))}
                      style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }} />
                  </td>
                  <td style={{ padding: "8px 16px" }}>
                    <input value={rowForm.description} onChange={e => setRowForm(f => ({ ...f, description: e.target.value }))}
                      style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }} />
                  </td>
                  <td style={{ padding: "8px 16px" }}>
                    <input type="number" min={0} step="0.01" value={rowForm.unit_price} onChange={e => setRowForm(f => ({ ...f, unit_price: e.target.value }))}
                      style={{ width: "100%", padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "8px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button onClick={() => updateMut.mutate({ id: p.id, data: { name: rowForm.name, description: rowForm.description, unit_price: parseFloat(rowForm.unit_price) || 0 } })}
                      disabled={updateMut.isPending}
                      style={{ background: "#1e3a8a", color: "white", border: "none", borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, marginRight: 6 }}>Save</button>
                    <button onClick={() => setEditingProduct(null)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, color: "#64748b" }}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                  onMouseLeave={e => (e.currentTarget.style.background = "white")}>
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1e293b" }}>{p.name}</td>
                  <td style={{ padding: "12px 16px", color: "#64748b", maxWidth: 320 }}>
                    <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.description || <span style={{ color: "#cbd5e1" }}>—</span>}</span>
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 600, color: "#0f766e" }}>{p.unit_price.toLocaleString("en", { minimumFractionDigits: 2 })}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button onClick={() => { setEditingProduct(p); setRowForm({ name: p.name, description: p.description || "", unit_price: String(p.unit_price) }); }}
                      style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600, marginRight: 6 }}>✏ Edit</button>
                    <AsyncButton onClick={async () => { if (await confirm(`Delete "${p.name}"?`, { confirmLabel: "Delete" })) deleteMut.mutate(p.id); }}
                      style={{ background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>✕ Delete</AsyncButton>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table></div>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 8 }}>No products yet.</div>
            <div style={{ fontSize: 13 }}>Add products/services to quickly fill line items on Quotations, Billing Notes, and Tax Invoices.</div>
          </div>
        )}
      </div>
    </div>
  );
}
